import { Link } from "react-router-dom";

import { ArrowRightIcon } from "@heroicons/react/24/outline";

import Card from "../shared/Card/Card";
import LoadingWheel from "../shared/LoadingWheel";
import useCustomerReadiness from "../../data/CustomerReadiness/useCustomerReadiness";

interface ReadinessSummaryCardProps {
  customerId: string;
}

/**
 * Compact onboarding score on the customer hub, linking through to the
 * full breakdown.
 */
export function ReadinessSummaryCard({
  customerId,
}: ReadinessSummaryCardProps) {
  const { readiness } = useCustomerReadiness(customerId);

  return (
    <Card
      title="Onboarding"
      labelId="readiness-summary"
      titleRightContent={
        <Link
          to={`/customer/${customerId}/readiness`}
          className="flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800"
        >
          Details
          <ArrowRightIcon className="h-4 w-4" />
        </Link>
      }
      bodyClassName="p-4"
    >
      {readiness.isLoading && (
        <div className="flex justify-center py-4">
          <LoadingWheel />
        </div>
      )}

      {readiness.isError && (
        <p className="py-4 text-center text-sm text-gray-500">
          Could not load readiness
        </p>
      )}

      {readiness.data && (
        <div className="py-1">
          <div className="flex items-baseline justify-center gap-1">
            <span className="text-5xl text-gray-900">
              {readiness.data.passed}
            </span>
            <span className="text-xl text-gray-400">
              / {readiness.data.total}
            </span>
          </div>

          <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-gray-100">
            <div
              className={`h-full rounded-full ${
                readiness.data.passed === readiness.data.total
                  ? "bg-green-600"
                  : "bg-blue-600"
              }`}
              style={{
                width: `${
                  readiness.data.total === 0
                    ? 100
                    : Math.round(
                        (readiness.data.passed / readiness.data.total) * 100,
                      )
                }%`,
              }}
            />
          </div>

          <p className="mt-2 text-center text-xs text-gray-500">
            {readiness.data.total - readiness.data.passed === 0
              ? "Nothing outstanding here"
              : `${readiness.data.total - readiness.data.passed} outstanding`}
          </p>
        </div>
      )}
    </Card>
  );
}
